/**
 * EcolesClassesPage — configuration des écoles et de leurs classes (manager/admin).
 */

import { useEffect, useState } from 'react';
import { Plus, School, Clock, MapPin } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Button, Input, Field, Alert, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { LieuSelect } from '@/components/LieuSelect';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import type { ScolaireEcole, ScolaireClasse, EcoleType } from '@roronoa-collectivites/shared-types';

const ECOLE_TYPES: { value: EcoleType; label: string }[] = [
  { value: 'maternelle', label: 'Maternelle' },
  { value: 'elementaire', label: 'Élémentaire' },
  { value: 'primaire', label: 'Primaire' },
  { value: 'centre_loisirs', label: 'Centre de loisirs / Lieu d’accueil (ALSH)' },
];

export function EcolesClassesPage() {
  const [ecoles, setEcoles] = useState<ScolaireEcole[]>([]);
  const [classesByEcole, setClassesByEcole] = useState<Record<string, ScolaireClasse[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [ecoleDialog, setEcoleDialog] = useState(false);
  const [classeDialog, setClasseDialog] = useState<ScolaireEcole | null>(null);
  const [nomEcole, setNomEcole] = useState('');
  const [typeEcole, setTypeEcole] = useState<EcoleType>('elementaire');
  const [nomClasse, setNomClasse] = useState('');
  const [niveauClasse, setNiveauClasse] = useState('');
  const [saving, setSaving] = useState(false);
  // Dialogue horaires d'accueil
  const [horairesDialog, setHorairesDialog] = useState<ScolaireEcole | null>(null);
  const [horairesMode, setHorairesMode] = useState<'ecole' | 'garderie' | 'alsh'>('ecole');
  const [hForm, setHForm] = useState<Record<string, string>>({});

  async function load() {
    try {
      const list = await api.scolaire.listEcoles();
      setEcoles(list);
      const map: Record<string, ScolaireClasse[]> = {};
      await Promise.all(list.map(async (e) => { map[e.id] = await api.scolaire.listClasses(e.id); }));
      setClassesByEcole(map);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  async function rattacherLieu(ecoleId: string, lieuId: string | null) {
    try {
      await api.scolaire.updateEcoleLieu(ecoleId, lieuId);
      await load();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur lors du rattachement au lieu');
    }
  }

  async function createEcole() {
    if (!nomEcole.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.scolaire.createEcole({ nom: nomEcole.trim(), type: typeEcole });
      setEcoleDialog(false); setNomEcole(''); setTypeEcole('elementaire');
      await load();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur lors de la création');
    } finally { setSaving(false); }
  }

  async function createClasse() {
    if (!classeDialog || !nomClasse.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.scolaire.createClasse({ ecoleId: classeDialog.id, nom: nomClasse.trim(), niveau: niveauClasse.trim() || undefined });
      setClasseDialog(null); setNomClasse(''); setNiveauClasse('');
      await load();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur lors de la création');
    } finally { setSaving(false); }
  }

  function ouvrirHoraires(e: ScolaireEcole, mode: 'ecole' | 'garderie' | 'alsh') {
    setHorairesMode(mode);
    if (mode === 'ecole') {
      const h = e.horaires;
      setHForm({
        ouverture: h?.ouverture ?? '', finMatin: h?.finMatin ?? '',
        repriseApresMidi: h?.repriseApresMidi ?? '', finJournee: h?.finJournee ?? '',
        mercrediOuverture: h?.mercrediOuverture ?? '', mercrediFin: h?.mercrediFin ?? '',
      });
    } else if (mode === 'garderie') {
      const g = e.garderie;
      setHForm({
        garderieOuvertureMatin: g?.ouvertureMatin ?? '', garderieFermetureSoir: g?.fermetureSoir ?? '',
        garderieMercrediOuverture: g?.mercrediOuverture ?? '', garderieMercrediFermeture: g?.mercrediFermeture ?? '',
      });
    } else {
      const a = e.alsh;
      setHForm({
        alshOuverture: a?.ouverture ?? '', alshFermeture: a?.fermeture ?? '',
        alshMercrediOuverture: a?.mercrediOuverture ?? '', alshMercrediFermeture: a?.mercrediFermeture ?? '',
      });
    }
    setHorairesDialog(e);
  }

  async function enregistrerHoraires() {
    if (!horairesDialog) return;
    setSaving(true); setError(null);
    try {
      const nz = (v: string | undefined) => (!v || v.trim() === '' ? null : v);
      // On ne transmet que les champs du bloc en cours d'édition.
      const body: Record<string, string | null> = {};
      for (const k of Object.keys(hForm)) body[k] = nz(hForm[k]);
      await api.scolaire.updateEcoleHoraires(horairesDialog.id, body);
      setHorairesDialog(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur lors de l’enregistrement');
    } finally { setSaving(false); }
  }

  return (
    <ScolaireLayout activeTab="ecoles">
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Écoles &amp; classes</h1>
            <p className="mt-1 text-sm text-ink-muted">Gérez les établissements et leurs classes.</p>
          </div>
          <Button variant="cantine" onClick={() => setEcoleDialog(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter un établissement
          </Button>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : ecoles.length === 0 ? (
          <Card><CardBody><p className="text-sm text-ink-muted">Aucun établissement. Commencez par en ajouter un (école ou centre de loisirs ALSH).</p></CardBody></Card>
        ) : (
          <div className="space-y-4">
            {ecoles.map((e) => (
              <Card key={e.id}>
                <CardBody>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <School className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
                      <span className="font-display text-base font-semibold text-ink">{e.nom}</span>
                      <Badge>{ECOLE_TYPES.find((t) => t.value === e.type)?.label ?? e.type}</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {e.type !== 'centre_loisirs' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => ouvrirHoraires(e, 'ecole')}>
                            <Clock className="h-4 w-4" aria-hidden="true" /> École
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => ouvrirHoraires(e, 'garderie')}>
                            <Clock className="h-4 w-4" aria-hidden="true" /> Garderie
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setClasseDialog(e)}>
                            <Plus className="h-4 w-4" aria-hidden="true" /> Classe
                          </Button>
                        </>
                      )}
                      {e.type === 'centre_loisirs' && (
                        <Button variant="ghost" size="sm" onClick={() => ouvrirHoraires(e, 'alsh')}>
                          <Clock className="h-4 w-4" aria-hidden="true" /> Horaires ALSH
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* S4-b : lieu rattaché (partage de bâtiment maternelle/élémentaire) */}
                  <div className="mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
                    <span className="text-xs text-ink-muted">Bâtiment :</span>
                    <LieuSelect
                      value={e.lieuId}
                      onChange={(lieuId) => void rattacherLieu(e.id, lieuId)}
                      className="h-8 max-w-xs rounded-lg border border-border bg-surface/60 px-2 text-xs text-ink"
                    />
                  </div>
                  {e.type === 'centre_loisirs' ? (
                    <p className="text-sm text-ink-subtle">Lieu d’accueil ALSH — reçoit les enfants de toutes les écoles. Pas de classe à déclarer ici.</p>
                  ) : (classesByEcole[e.id] ?? []).length === 0 ? (
                    <p className="text-sm text-ink-subtle">Aucune classe.</p>
                  ) : (
                    <ul className="flex flex-wrap gap-2">
                      {(classesByEcole[e.id] ?? []).map((c) => (
                        <li key={c.id} className="rounded-r-md border border-border bg-surface/40 px-3 py-1.5 text-sm text-ink">
                          {c.nom}{c.niveau ? <span className="ml-1 text-ink-subtle">· {c.niveau}</span> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                  {e.type !== 'centre_loisirs' && e.horaires && (e.horaires.ouverture || e.horaires.finJournee) && (
                    <p className="mt-3 border-t border-border/50 pt-2 text-xs text-ink-subtle">
                      <Clock className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden="true" />
                      École :{' '}
                      {e.horaires.ouverture && e.horaires.finMatin ? `${e.horaires.ouverture}–${e.horaires.finMatin}` : ''}
                      {e.horaires.repriseApresMidi && e.horaires.finJournee ? ` · ${e.horaires.repriseApresMidi}–${e.horaires.finJournee}` : ''}
                      {e.horaires.mercrediOuverture && e.horaires.mercrediFin ? ` · Mer. ${e.horaires.mercrediOuverture}–${e.horaires.mercrediFin}` : ''}
                    </p>
                  )}
                  {e.type !== 'centre_loisirs' && e.garderie && (e.garderie.ouvertureMatin || e.garderie.fermetureSoir) && (
                    <p className="mt-1 text-xs text-ink-subtle">
                      <Clock className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden="true" />
                      Garderie :{' '}
                      {e.garderie.ouvertureMatin ? `dès ${e.garderie.ouvertureMatin}` : ''}
                      {e.garderie.fermetureSoir ? ` · jusqu'à ${e.garderie.fermetureSoir}` : ''}
                      {e.garderie.mercrediOuverture && e.garderie.mercrediFermeture ? ` · Mer. ${e.garderie.mercrediOuverture}–${e.garderie.mercrediFermeture}` : ''}
                    </p>
                  )}
                  {e.type === 'centre_loisirs' && e.alsh && (e.alsh.ouverture || e.alsh.fermeture) && (
                    <p className="mt-3 border-t border-border/50 pt-2 text-xs text-ink-subtle">
                      <Clock className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden="true" />
                      ALSH :{' '}
                      {e.alsh.ouverture && e.alsh.fermeture ? `${e.alsh.ouverture}–${e.alsh.fermeture}` : ''}
                      {e.alsh.mercrediOuverture && e.alsh.mercrediFermeture ? ` · Mer. ${e.alsh.mercrediOuverture}–${e.alsh.mercrediFermeture}` : ''}
                    </p>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Dialog création école */}
      <Dialog
        open={ecoleDialog}
        onClose={() => setEcoleDialog(false)}
        title="Nouvel établissement"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEcoleDialog(false)}>Annuler</Button>
            <Button variant="cantine" loading={saving} onClick={createEcole}>Créer</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Nom de l'établissement" htmlFor="nom-ecole" required>
            <Input id="nom-ecole" value={nomEcole} onChange={(ev) => setNomEcole(ev.target.value)} placeholder="École élémentaire Jean Jaurès / Centre de loisirs Les Lutins" />
          </Field>
          <Field label="Type" htmlFor="type-ecole">
            <select
              id="type-ecole"
              value={typeEcole}
              onChange={(ev) => setTypeEcole(ev.target.value as EcoleType)}
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-cantine focus:ring-offset-2 focus:ring-offset-bg"
            >
              {ECOLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>
      </Dialog>

      {/* Dialog création classe */}
      <Dialog
        open={!!classeDialog}
        onClose={() => setClasseDialog(null)}
        title={`Nouvelle classe — ${classeDialog?.nom ?? ''}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setClasseDialog(null)}>Annuler</Button>
            <Button variant="cantine" loading={saving} onClick={createClasse}>Créer</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Nom de la classe" htmlFor="nom-classe" required>
            <Input id="nom-classe" value={nomClasse} onChange={(ev) => setNomClasse(ev.target.value)} placeholder="CE2 A" />
          </Field>
          <Field label="Niveau (optionnel)" htmlFor="niveau-classe">
            <Input id="niveau-classe" value={niveauClasse} onChange={(ev) => setNiveauClasse(ev.target.value)} placeholder="CE2" />
          </Field>
        </div>
      </Dialog>

      {/* Dialog horaires d'accueil (mode école / garderie / ALSH) */}
      <Dialog
        open={!!horairesDialog}
        onClose={() => setHorairesDialog(null)}
        title={`Horaires ${horairesMode === 'ecole' ? 'École' : horairesMode === 'garderie' ? 'Garderie' : 'ALSH'} — ${horairesDialog?.nom ?? ''}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setHorairesDialog(null)}>Annuler</Button>
            <Button variant="cantine" loading={saving} onClick={enregistrerHoraires}>Enregistrer</Button>
          </>
        }
      >
        <div className="space-y-4">
          {horairesMode === 'ecole' && (
            <>
              <p className="text-xs text-ink-muted">Heures de classe. Servent de repère (départ du matin du périscolaire = ouverture de l'école) et sont affichées aux familles. Laissez vide si non concerné.</p>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink">Lundi, mardi, jeudi, vendredi</span>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ouverture (matin)" htmlFor="h-ouv"><Input id="h-ouv" type="time" value={hForm.ouverture ?? ''} onChange={(e) => setHForm((f) => ({ ...f, ouverture: e.target.value }))} /></Field>
                  <Field label="Fin de matinée" htmlFor="h-fm"><Input id="h-fm" type="time" value={hForm.finMatin ?? ''} onChange={(e) => setHForm((f) => ({ ...f, finMatin: e.target.value }))} /></Field>
                  <Field label="Reprise (après-midi)" htmlFor="h-rep"><Input id="h-rep" type="time" value={hForm.repriseApresMidi ?? ''} onChange={(e) => setHForm((f) => ({ ...f, repriseApresMidi: e.target.value }))} /></Field>
                  <Field label="Fin de journée" htmlFor="h-fj"><Input id="h-fj" type="time" value={hForm.finJournee ?? ''} onChange={(e) => setHForm((f) => ({ ...f, finJournee: e.target.value }))} /></Field>
                </div>
              </div>
              <div className="border-t border-border/60 pt-3">
                <span className="mb-1.5 block text-sm font-medium text-ink">Mercredi <span className="font-normal text-ink-subtle">(souvent matin seul)</span></span>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ouverture" htmlFor="h-mo"><Input id="h-mo" type="time" value={hForm.mercrediOuverture ?? ''} onChange={(e) => setHForm((f) => ({ ...f, mercrediOuverture: e.target.value }))} /></Field>
                  <Field label="Fin" htmlFor="h-mf"><Input id="h-mf" type="time" value={hForm.mercrediFin ?? ''} onChange={(e) => setHForm((f) => ({ ...f, mercrediFin: e.target.value }))} /></Field>
                </div>
              </div>
            </>
          )}
          {horairesMode === 'garderie' && (
            <>
              <p className="text-xs text-ink-muted">Heures d'accueil de la garderie (périscolaire). L'arrivée du matin se pré-remplit avec l'ouverture garderie ; le départ du matin avec l'ouverture de l'école. Laissez vide si non concerné.</p>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink">Lundi, mardi, jeudi, vendredi</span>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ouverture (matin)" htmlFor="g-om"><Input id="g-om" type="time" value={hForm.garderieOuvertureMatin ?? ''} onChange={(e) => setHForm((f) => ({ ...f, garderieOuvertureMatin: e.target.value }))} /></Field>
                  <Field label="Fermeture (soir)" htmlFor="g-fs"><Input id="g-fs" type="time" value={hForm.garderieFermetureSoir ?? ''} onChange={(e) => setHForm((f) => ({ ...f, garderieFermetureSoir: e.target.value }))} /></Field>
                </div>
              </div>
              <div className="border-t border-border/60 pt-3">
                <span className="mb-1.5 block text-sm font-medium text-ink">Mercredi</span>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ouverture" htmlFor="g-mo"><Input id="g-mo" type="time" value={hForm.garderieMercrediOuverture ?? ''} onChange={(e) => setHForm((f) => ({ ...f, garderieMercrediOuverture: e.target.value }))} /></Field>
                  <Field label="Fermeture" htmlFor="g-mf"><Input id="g-mf" type="time" value={hForm.garderieMercrediFermeture ?? ''} onChange={(e) => setHForm((f) => ({ ...f, garderieMercrediFermeture: e.target.value }))} /></Field>
                </div>
              </div>
            </>
          )}
          {horairesMode === 'alsh' && (
            <>
              <p className="text-xs text-ink-muted">Heures d'accueil de l'ALSH (centre de loisirs). Affichées aux familles. Laissez vide si non concerné.</p>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink">Jours d'ouverture</span>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ouverture" htmlFor="a-o"><Input id="a-o" type="time" value={hForm.alshOuverture ?? ''} onChange={(e) => setHForm((f) => ({ ...f, alshOuverture: e.target.value }))} /></Field>
                  <Field label="Fermeture" htmlFor="a-f"><Input id="a-f" type="time" value={hForm.alshFermeture ?? ''} onChange={(e) => setHForm((f) => ({ ...f, alshFermeture: e.target.value }))} /></Field>
                </div>
              </div>
              <div className="border-t border-border/60 pt-3">
                <span className="mb-1.5 block text-sm font-medium text-ink">Mercredi</span>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ouverture" htmlFor="a-mo"><Input id="a-mo" type="time" value={hForm.alshMercrediOuverture ?? ''} onChange={(e) => setHForm((f) => ({ ...f, alshMercrediOuverture: e.target.value }))} /></Field>
                  <Field label="Fermeture" htmlFor="a-mf"><Input id="a-mf" type="time" value={hForm.alshMercrediFermeture ?? ''} onChange={(e) => setHForm((f) => ({ ...f, alshMercrediFermeture: e.target.value }))} /></Field>
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </ScolaireLayout>
  );
}
